import { ReactNode } from 'react'
import { Box, Stack, useTheme } from '@mui/material'
import {
  DatumValue,
  LineSvgProps,
  Point,
  ResponsiveLine,
  SliceTooltipProps,
} from '@nivo/line'
import constants from './chart-constants'
import TooltipCard from './TooltipCard'

export type LineChartProps = {
  dataHasColor?: boolean
  height?: number | string
  label?: ReactNode
  tooltipContent?: (
    point: Point,
    stackedPoints: {
      id: string | number
      color?: string
      x?: DatumValue | null | undefined
      y?: DatumValue | null | undefined
    }[],
  ) => ReactNode
  sliceTooltipContent?: (data: SliceTooltipProps) => ReactNode
  xLegend?: string
  yLegend?: string
} & Omit<LineSvgProps, 'width' | 'height'>

export default function LineChart({
  data,
  dataHasColor,
  height = 420,
  label,
  sliceTooltip,
  tooltip,
  tooltipContent,
  sliceTooltipContent,
  axisBottom,
  axisLeft,
  margin,
  xFormat,
  xLegend,
  yFormat,
  yLegend,
  ...props
}: LineChartProps) {
  const theme = useTheme()
  const hasData = data.length > 0 && data.some((serie) => serie.data.length > 0)
  const noDataProps = !hasData
    ? {
        data: [
          {
            id: 'empty',
            data: [
              { x: 0, y: 0 },
              { x: 1, y: 0 },
            ],
          },
        ],
        colors: ['transparent'],
        enablePoints: false,
        enableGridX: false,
        enableGridY: false,
        isInteractive: false,
        xScale: { min: 0, max: 1, type: 'linear' as const },
        yScale: { min: 0, max: 1, type: 'linear' as const },
        margin: {
          top: constants.MARGIN_TOP,
          bottom: 30,
          right: constants.MARGIN_RIGHT,
          left: 30,
        },
        markers: [
          {
            axis: 'y' as const,
            value: 0.5,
            lineStyle: {
              stroke: 'transparent',
            },
            legend: 'No Data',
            textStyle: {
              fill: '#ffffff',
            },
            legendPosition: 'top' as const,
            legendOrientation: 'horizontal' as const,
          },
        ],
      }
    : {}

  return (
    <Box sx={{ height }}>
      {label && (
        <Stack direction="row" spacing={0.5}>
          {label}
        </Stack>
      )}
      {/* ResponsiveLine needs to be wrapped by a container with a set height */}
      <ResponsiveLine
        data={data}
        colors={dataHasColor ? (data) => data.color : undefined}
        margin={{
          top: constants.MARGIN_TOP,
          right: constants.MARGIN_RIGHT,
          bottom: xLegend
            ? constants.MARGIN_BOTTOM_WITH_LEGEND
            : constants.MARGIN_BOTTOM,
          left: yLegend
            ? constants.MARGIN_LEFT_WITH_LEGEND
            : constants.MARGIN_LEFT,
          ...margin,
        }}
        xFormat={xFormat}
        axisBottom={
          hasData
            ? {
                format: xFormat,
                legend: xLegend,
                legendPosition: 'middle',
                legendOffset: 40,
                ...axisBottom,
              }
            : {
                tickValues: [],
              }
        }
        yFormat={yFormat}
        axisLeft={{
          format: yFormat,
          legend: yLegend,
          legendPosition: 'middle',
          legendOffset: -50,
          ...axisLeft,
          tickValues: hasData ? axisLeft?.tickValues : [],
        }}
        pointSize={9}
        sliceTooltip={
          sliceTooltipContent
            ? (data) => <TooltipCard>{sliceTooltipContent(data)}</TooltipCard>
            : sliceTooltip
        }
        tooltip={
          tooltipContent
            ? ({ point }) => {
                const allPointsData = data.flatMap(({ id, color, data }) =>
                  data.map((node) => ({ ...node, id, color })),
                )
                const stackedPoints = allPointsData.filter(
                  (pointData) =>
                    pointData.x == point.data.x && pointData.y == point.data.y,
                )
                return (
                  <TooltipCard>
                    {tooltipContent(point, stackedPoints)}
                  </TooltipCard>
                )
              }
            : tooltip
        }
        theme={{
          axis: {
            domain: {
              line: {
                stroke: theme.palette.divider,
              },
            },
            legend: {
              text: {
                fontSize: 14,
                fill: '#d2d2d2',
              },
            },
            ticks: {
              text: {
                fill: theme.palette.text.secondary,
              },
            },
          },
          crosshair: {
            line: {
              stroke: theme.palette.text.primary,
            },
          },
          grid: {
            line: {
              stroke: theme.palette.divider,
            },
          },
        }}
        useMesh
        {...noDataProps}
        {...props}
      />
    </Box>
  )
}
